import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import inputStyles from "../../../../assets/styles/components/input.module.css";
import Button from "../../../../components/Button/Button";
import styles from "./IncomingPayments.module.css";

import { Add, ArrowBack, FilterAlt, FilterAltOffSharp, Search } from "@mui/icons-material";

import { toast } from "react-toastify";
import {
  cancelFinancialEntry,
  fetchPaymentTypeByTitle,
  loadFinancialEntries,
  markFinancialEntryAsPaid,
  refoundFinancialEntry,
} from "../../../../services/financial";
import type { movimentType } from "../../../../types/cashFlow";
import type {
  Iordem,
  IFinancialEntry,
  IFinancialFilter,
  ILoadFinancialEntriesResponse,
} from "../../../../types/financial";
import Modal from "../CashFlow/Modal";
import FinancialFilterModal from "./FinancialFilterModal/FinancialFilterModal";
import PaymentModal from "./PaymentTypeModal/PaymentTypeModal";
import FinancialEntriesList from "./FinancialEntriesList";
import InputSearch from "../../../../components/InputSearch/InputSearch";

// ─── Chaves do sessionStorage ────────────────────────────────────────────────
const SESSION_PAGES_KEY = "incomingPayments_pages";
const SESSION_SCROLL_KEY = "incomingPayments_scroll";

const getTodayFormatted = () => {
  const today = new Date();
  const year = today.getUTCFullYear();
  const month = String(today.getUTCMonth() + 1).padStart(2, "0");
  const day = String(today.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

type FinancialStatusFilter = "aberto" | "pago" | "atrasado" | "cancelado";
type CardFilterType = FinancialStatusFilter | "vencemHoje";

const statusMapFinancial = (
  status?: "aberto" | "pago" | "atrasado" | "cancelado" | "pagar" | "receber",
) => {
  switch (status) {
    case "aberto":
      return { text: "Em aberto", color: "blue-finance" };
    case "pago":
      return { text: "Pago", color: "green-finance" };
    case "atrasado":
      return { text: "Atrasado", color: "red-finance" };
    case "cancelado":
      return { text: "Cancelado", color: "gray-finance" };
    case "receber":
      return { text: "A Receber", color: "green-type" };
    case "pagar":
      return { text: "A Pagar", color: "yellow-type" };
    default:
      return { text: "N/A", color: "default" };
  }
};

// ─── Helpers de (de)serialização de URL params ───────────────────────────────
type OrderField = keyof Iordem;

function buildOrderFromParams(params: URLSearchParams): Iordem {
  const field = params.get("orderField") as OrderField | null;
  const dir = params.get("orderDir") as "ASC" | "DESC" | null;
  if (field && dir) {
    return { [field]: dir } as Iordem;
  }
  return { codigo_estabelecimento: "DESC" };
}

function buildFilterFromParams(params: URLSearchParams): IFinancialFilter {
  return {
    tipo: (params.get("tipo") as any) || "receber",
    uid_cliente: params.get("uid_cliente") || undefined,
    status: (params.get("status") as any) || undefined,
    uid_tipo_pagamento: params.get("uid_tipo_pagamento") || undefined,
    date_initial: params.get("date_initial") || undefined,
    date_final: params.get("date_final") || undefined,
    uid_conta_financeira: params.get("uid_conta_financeira") || undefined,
    uid_plano_contas: params.get("uid_plano_contas") || undefined,
    uid_centro_custo: params.get("uid_centro_custo") || undefined,
    // @ts-ignore
    period: params.get("period") || undefined,
  };
}

const IncomingPayments = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Estado derivado da URL ──────────────────────────────────────────────────
  const search = searchParams.get("search") ?? "";
  const filterValues = useMemo(() => buildFilterFromParams(searchParams), [searchParams]);
  const orderTable = useMemo(() => buildOrderFromParams(searchParams), [searchParams]);

  // ── Estado local de UI ─────────────────────────────────────────────────────
  const [isHidden, setIsHidden] = useState<{
    filter?: boolean;
    payment?: boolean;
    movement?: boolean;
  }>({
    filter: false,
    payment: true,
    movement: true,
  });

  const [tempSearch, setTempSearch] = useState(search);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [entryToRefound, setEntryToRefound] = useState<IFinancialEntry | undefined>(undefined);
  const [entryToPay, setEntryToPay] = useState<IFinancialEntry | undefined>(undefined);
  const [, setSuccessMessage] = useState<string | null>(null);
  const [, setErrorMessage] = useState<string | null>(null);
  const [tempPaymentData, setTempPaymentData] = useState<{
    uidType?: string;
    titleType?: string;
  } | null>(null);

  // ── Ref para o container com scroll ────────────────────────────────────────
  const tbodyRef = useRef<any>(null);

  // ── Controle de restauração ─────────────────────────────────────────────────
  const [targetPages, setTargetPages] = useState<number>(() => {
    const saved = sessionStorage.getItem(SESSION_PAGES_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });
  const [scrollRestored, setScrollRestored] = useState(false);
  const isRestoringRef = useRef(targetPages > 1);

  // ─── Helpers para atualizar URL ────────────────────────────────────────────
  const updateParams = (updates: Record<string, string | boolean | null | undefined>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  };

  // ─── Infinite query ────────────────────────────────────────────────────────
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, refetch } =
    useInfiniteQuery<ILoadFinancialEntriesResponse>({
      queryKey: ["financialEntries", search, filterValues, orderTable],
      queryFn: ({ pageParam }) =>
        loadFinancialEntries(search, pageParam as number, filterValues, orderTable),
      getNextPageParam: (lastPage, allPages) => {
        const totalCarregado = allPages.reduce((acc, page) => acc + page.rows.length, 0);
        return totalCarregado < lastPage.count ? allPages.length + 1 : undefined;
      },
      initialPageParam: 1,
      refetchOnWindowFocus: true,
    });

  const isLoading = isFetching && !isFetchingNextPage;

  const dataRender: IFinancialEntry[] = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) => page.rows);
  }, [data]);

  const financialStats = useMemo(() => {
    return (
      data?.pages?.[0]?.stats ?? { vencidos: 0, vencemHoje: 0, aVencer: 0, pagos: 0, total: 0 }
    );
  }, [data]);

  // ─── Restauração de páginas ────────────────────────────────────────────────
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    sessionStorage.removeItem(SESSION_PAGES_KEY);
    sessionStorage.removeItem(SESSION_SCROLL_KEY);
    setTargetPages(1);
    setScrollRestored(false);
    isRestoringRef.current = false;
  }, [search, filterValues, orderTable]);

  useEffect(() => {
    if (!isRestoringRef.current) return;
    if (!data) return;

    const currentPages = data.pages.length;
    if (currentPages < targetPages && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    } else if (currentPages >= targetPages) {
      isRestoringRef.current = false;
    }
  }, [data, targetPages, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Salvar quantidade de páginas no sessionStorage
  useEffect(() => {
    if (isRestoringRef.current) return;
    const pages = data?.pages.length ?? 1;
    sessionStorage.setItem(SESSION_PAGES_KEY, String(pages));
  }, [data?.pages.length]);

  // ─── Restauração do scroll após páginas carregadas ─────────────────────────
  useEffect(() => {
    if (scrollRestored) return;
    if (isRestoringRef.current) return;

    const el = tbodyRef.current;
    if (!el) return;

    const savedScroll = sessionStorage.getItem(SESSION_SCROLL_KEY);
    if (!savedScroll) {
      setScrollRestored(true);
      return;
    }

    const scrollY = parseInt(savedScroll, 10);

    requestAnimationFrame(() => {
      el.scrollTop = scrollY;
      setScrollRestored(true);
    });
  }, [data?.pages.length, scrollRestored]);

  // ─── Salvar posição do scroll (debounce 200ms) ─────────────────────────────
  useEffect(() => {
    const el = tbodyRef.current;
    if (!el) return;

    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        sessionStorage.setItem(SESSION_SCROLL_KEY, String(el.scrollTop));
      }, 200);
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", handleScroll);
      clearTimeout(debounceTimer);
    };
  }, []);

  // ─── Debounce da busca ─────────────────────────────────────────────────────
  useEffect(() => {
    const handler = setTimeout(() => {
      updateParams({ search: tempSearch || undefined });
    }, 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempSearch]);

  const { mutate: mutate_refound } = useMutation({
    mutationFn: async (uid: string) => await refoundFinancialEntry(uid),
    onSuccess: async (response) => {
      toast.success(response.message);
      setEntryToRefound(undefined);
      await refetch();
    },
    onError: async (err) => {
      toast.error(err.message);
      setEntryToRefound(undefined);
      await refetch();
    },
  });

  useEffect(() => {
    setIsFilterApplied(
      filterValues.uid_cliente ||
        filterValues.status ||
        filterValues.uid_tipo_pagamento ||
        filterValues.date_initial ||
        filterValues.date_final
        ? true
        : false,
    );
  }, [filterValues]);

  // ─── Handlers de Filtros e URL ─────────────────────────────────────────────
  const handleResetFilter = () => {
    setTempSearch("");
    setSearchParams(new URLSearchParams({ tipo: "receber" }), { replace: true });
  };

  const handleCardClick = (filterType: CardFilterType) => {
    const todayFormatted = getTodayFormatted();
    const statusOnlyFilters: FinancialStatusFilter[] = ["atrasado", "aberto", "pago"];

    const isVencemHojeFilterActive =
      filterType === "vencemHoje" &&
      filterValues.date_initial === todayFormatted &&
      filterValues.date_final === todayFormatted &&
      filterValues.status === "aberto";

    const isStatusOnlyFilterActive =
      statusOnlyFilters.includes(filterType as FinancialStatusFilter) &&
      filterValues.status === filterType &&
      !filterValues.date_initial &&
      !filterValues.date_final;

    let nextFilters: Record<string, any> = { tipo: "receber" };

    if (isStatusOnlyFilterActive || isVencemHojeFilterActive) {
      nextFilters = { ...filterValues, status: undefined, date_initial: undefined, date_final: undefined, tipo: "receber" };
    } else if (filterType === "vencemHoje") {
      nextFilters = { ...filterValues, status: "aberto", date_initial: todayFormatted, date_final: todayFormatted, tipo: "receber" };
    } else if (statusOnlyFilters.includes(filterType as FinancialStatusFilter)) {
      nextFilters = { ...filterValues, status: filterType, date_initial: undefined, date_final: undefined, tipo: "receber" };
    }

    // @ts-ignore
    updateParams(nextFilters);
  };

  const handleSetFilterModal = (newFilter: IFinancialFilter) => {
    updateParams({ ...newFilter, tipo: "receber" });
  };

  const handleSetOrderTable = (updater: any) => {
    const nextOrder = typeof updater === "function" ? updater(orderTable) : updater;
    const field = Object.keys(nextOrder)[0];
    const dir = nextOrder[field];
    updateParams({ orderField: field, orderDir: dir });
  };

  const handleCancelEntry = async (uid: string) => {
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      if (!window.confirm("Tem certeza que deseja cancelar este lançamento financeiro?")) return;
      await cancelFinancialEntry(uid);
      handleSuccess("Lançamento financeiro cancelado com sucesso!");
      await refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erro desconhecido ao cancelar lançamento.";
      console.error("Erro ao cancelar:", message);
      setErrorMessage(message);
      await refetch();
    }
  };

  useEffect(() => {
    if (!entryToRefound) return;
    mutate_refound(entryToRefound.uid_financeiro_lancamento);
  }, [entryToRefound]);

  const handleOpenPaymentModal = (entry: IFinancialEntry) => {
    setEntryToPay(entry);
    const paymentTypeTitle = entry.tipo_pagamento?.titulo;
    if (paymentTypeTitle) {
      handleProceedToMovement(paymentTypeTitle, undefined, entry);
    } else {
      setIsHidden((prev) => ({ ...prev, payment: false }));
    }
  };

  const handleClosePaymentModal = () => {
    setEntryToPay(undefined);
    setTempPaymentData(null);
    setIsHidden((prev) => ({ ...prev, payment: true }));
  };

  const handleCloseMovementModal = () => {
    setIsHidden((prev) => ({ ...prev, movement: true }));
  };

  const handleProceedToMovement = async (
    paymentTitle: string,
    paymentUid?: string,
    entryOverride?: IFinancialEntry,
  ) => {
    const targetEntry = entryOverride || entryToPay;
    if (!targetEntry) return;

    let finalPaymentUid = paymentUid;
    let finalPaymentTitle = paymentTitle;

    if (!paymentUid && paymentTitle !== "Sem definição") {
      try {
        const paymentType = await fetchPaymentTypeByTitle(paymentTitle);
        if (paymentType) {
          finalPaymentUid = paymentType.uid_tipo_pagamento;
          finalPaymentTitle = paymentType.titulo;
        }
      } catch (error) {
        console.error("Erro ao buscar tipo de pagamento", error);
      }
    }

    setTempPaymentData({ uidType: finalPaymentUid, titleType: finalPaymentTitle });
    setIsHidden((prev) => ({ ...prev, payment: true, movement: false }));
  };

  const handleFinalizePayment = async (data_movimentacao?: string | null) => {
    if (!entryToPay) return;
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsHidden((prev) => ({ ...prev, movement: true }));

    try {
      const entryId = entryToPay.uid_financeiro_lancamento;
      const paymentDate = data_movimentacao ?? new Date().toISOString();

      await markFinancialEntryAsPaid(entryId!, paymentDate, tempPaymentData?.uidType);

      const successMsg = tempPaymentData?.titleType
        ? `Lançamento baixado e movimentação gerada com sucesso via ${tempPaymentData.titleType}!`
        : `Lançamento baixado e movimentação gerada com sucesso!`;

      handleSuccess(successMsg);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erro desconhecido ao finalizar baixa.";
      console.error("Erro ao quitar:", message);
      setErrorMessage(message);
    } finally {
      setEntryToPay(undefined);
      setTempPaymentData(null);
      await refetch();
    }
  };

  const getMovementInitialData = (): Partial<movimentType> | undefined => {
    if (!entryToPay) return undefined;
    return {
      descricao: `Baixa Ref: ${entryToPay.descricao} - Cliente: ${entryToPay.cliente?.nome || "N/A"}`,
      valor: entryToPay.valor_parcela,
      tipo: entryToPay.tipo === "receber" ? "entrada" : "saida",
      data_movimentacao: new Date().toISOString().split("T")[0],
      uid_conta_financeira: entryToPay.uid_conta_financeira || "",
      uid_financeiro_lancamento: entryToPay.uid_financeiro_lancamento,
      uid_plano_contas: entryToPay.uid_plano_contas,
      uid_centro_custo: entryToPay.uid_centro_custo,
    };
  };

  const handleRefound = (entry: IFinancialEntry): void => {
    setEntryToRefound(entry);
  };

  const handleSuccess = (message: string) => {
    setSuccessMessage(message);
    console.log(message);
  };

  return (
    <div className={styles.containerProjects}>
      <div className={styles.purchases}>
        <div className={styles.header}>
          <h1>Contas a Receber</h1>

          <InputSearch
            searchValue={tempSearch}
            setSearchValue={setTempSearch}
            placeholder="Pesquisar"
          />

          <div className={styles.headerButtons}>
            <p>Qtd:<span>{data?.pages?.[0]?.count ?? 0}</span></p>
            <Button variant="contain" onClick={handleResetFilter} title="Limpar filtros">
              <FilterAltOffSharp />
            </Button>
            <Button
              variant="contain"
              title="Filtrar lançamentos"
              onClick={() => setIsHidden((prev) => ({ ...prev, filter: true }))}
            >
              <FilterAlt />
            </Button>
            <Button
              variant="contain"
              onClick={() => navigate("add")}
              title="Adicionar novo lançamento"
            >
              <Add />
            </Button>
            <Button variant="contain" title="Voltar para página anterior" onClick={() => navigate(-1)}>
              <ArrowBack />
            </Button>
          </div>
        </div>

        <div className={styles.financialStatsCard}>
          <div className={styles.statLine}>
            {(() => {
              const todayFormatted = getTodayFormatted();
              const isVencemHojeActive =
                filterValues.status === "aberto" &&
                filterValues.date_initial === todayFormatted &&
                filterValues.date_final === todayFormatted;
              return (
                <>
                  <div
                    className={`${styles.statItem} ${styles.vencidosCard} ${filterValues.status === "atrasado" ? styles.activeFilter : ""}`}
                    onClick={() => handleCardClick("atrasado")}
                  >
                    <span className={styles.vencidosText}>Vencidos</span>
                    <span className={styles.vencidosCount}>{isLoading ? "..." : financialStats.vencidos}</span>
                  </div>
                  <div
                    className={`${styles.statItem} ${styles.vencemHojeCard} ${isVencemHojeActive ? styles.activeFilter : ""}`}
                    onClick={() => handleCardClick("vencemHoje")}
                  >
                    <span className={styles.vencemHojeText}>Vencem Hoje</span>
                    <span className={styles.vencemHojeCount}>{isLoading ? "..." : financialStats.vencemHoje}</span>
                  </div>
                  <div
                    className={`${styles.statItem} ${styles.aVencerCard} ${filterValues.status === "aberto" && !isVencemHojeActive ? styles.activeFilter : ""}`}
                    onClick={() => handleCardClick("aberto")}
                  >
                    <span className={styles.aVencerText}>Em Aberto</span>
                    <span className={styles.aVencerCount}>{isLoading ? "..." : financialStats.aVencer}</span>
                  </div>
                  <div
                    className={`${styles.statItem} ${styles.pagosCard} ${filterValues.status === "pago" ? styles.activeFilter : ""}`}
                    onClick={() => handleCardClick("pago")}
                  >
                    <span className={styles.pagosText}>Pagos</span>
                    <span className={styles.pagosCount}>{isLoading ? "..." : financialStats.pagos}</span>
                  </div>
                </>
              );
            })()}

            <div className={`${styles.statItem} ${styles.totalCard} ${styles.totalCardVisual}`}>
              <span className={styles.totalText}>Total</span>
              <span
                className={styles.totalCount}
                style={financialStats.total && financialStats.total < 1000000 ? { fontSize: "1.9rem" } : { fontSize: "1.5rem" }}
              >
                {isLoading
                  ? "..."
                  : isFilterApplied
                    ? financialStats.total
                      ? financialStats.total.toLocaleString("pt-br", { style: "currency", currency: "BRL" })
                      : "-"
                    : "-"}
              </span>
            </div>
          </div>
        </div>

        <FinancialEntriesList
          tbodyRef={tbodyRef}
          rows={dataRender}
          handleCancelEntry={handleCancelEntry}
          statusMapper={statusMapFinancial}
          handleRefound={handleRefound}
          handleCopyEntry={(entry) => {
            const entryCopy = {
              ...entry,
              uid_financeiro_lancamento: "",
              data_emissao: getTodayFormatted(),
              data_competencia: null,
              data_vencimento: "",
              data_pagamento: null,
              status: "aberto",
            };
            navigate("add", { state: { entryToCopy: entryCopy } });
          }}
          handlePayEntry={handleOpenPaymentModal}
          orderTable={orderTable}
          setOrderTable={handleSetOrderTable}
        />

        <div className={styles.loadMore}>
          {hasNextPage ? (
            <Button variant="contain" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
              {isFetchingNextPage ? "Carregando..." : "Carregar mais registros"}
            </Button>
          ) : dataRender.length > 0 ? (
            <p className={styles.tableLastItem}>Todos os registros foram carregados</p>
          ) : null}
        </div>
      </div>

      {isHidden.filter && (
        <FinancialFilterModal
          setValuesInput={handleSetFilterModal}
          valuesInput={filterValues}
          onClose={() => setIsHidden((prev) => ({ ...prev, filter: false }))}
        />
      )}
      {!isHidden.payment && entryToPay && (
        <PaymentModal
          onClickModal={handleClosePaymentModal}
          onPaymentSelected={(title) => handleProceedToMovement(title)}
          allowSkip={true}
        />
      )}
      {!isHidden.movement && entryToPay && (
        <Modal
          title="Gerar Movimentação (Baixa)"
          onclose={handleCloseMovementModal}
          initialData={getMovementInitialData()}
          onSuccessExternal={handleFinalizePayment}
          lockEntryType={true}
        />
      )}
    </div>
  );
};

export default IncomingPayments;
